/**
 * Market crisis-resource loader for intake safety screens.
 * Load only versioned resources from the approved V2 registry.
 * If the registry is unavailable or empty, return an explicit unavailable state;
 * never invent or hardcode emergency contact details.
 */
import store from '@/store'
import {isMatchmakingV2ApiEnabled, isSandboxMatchEnabled} from '@/intake/sandboxMatchApi'

function truthyEnv(value) {
  if (value === true || value === 1) {
    return true
  }
  const s = String(value == null ? '' : value).toLowerCase()
  return s === 'true' || s === '1' || s === 'yes'
}

function requestType() {
  if (isMatchmakingV2ApiEnabled()) {
    return 'matchmaking_v2'
  }
  return 'matchmaking_v2_sandbox'
}

function unwrapSuccess(resp) {
  if (resp && resp.type === 'SUCCESS') {
    return resp.data || {}
  }
  const err = new Error((resp && resp.message) || 'FAILURE')
  err.status = resp && resp.status_code
  err.data = (resp && resp.data) || {}
  err.code = err.data.code || null
  throw err
}

/**
 * Map registry rows to UI contact cards.
 * @param {Array<Object>} resources
 * @returns {Array<{id:string,tel:string,label:string,displayName:string}>}
 */
export function mapCrisisResources(resources) {
  if (!Array.isArray(resources)) {
    return []
  }
  return resources.map((row, index) => {
    const code = String(row.service_code || ('resource_' + index)).toLowerCase()
    const phone = String(row.phone || '').replace(/[^\d+]/g, '')
    const dial = String(row.e164_or_local_dial || '')
    const tel = dial.replace(/^tel:/i, '') || phone
    return {
      id: code,
      tel: tel || phone,
      label: code,
      displayName: row.display_name || code,
      availability: row.availability_label || '',
      priority: Number(row.priority) || index + 1,
    }
  }).filter((item) => !!item.tel)
}

/**
 * @typedef {{contacts: Array, registryUnavailable: boolean, usedFallback: boolean, reason: string|null}} CrisisLoadResult
 */

/**
 * @param {string} [marketId]
 * @returns {Promise<CrisisLoadResult>}
 */
export function loadCrisisResourceResult(marketId) {
  const market = marketId || 'IR'
  if (!isSandboxMatchEnabled() && !truthyEnv(process.env.VUE_APP_MATCHMAKING_V2_API)) {
    return Promise.resolve({contacts: [], registryUnavailable: true, usedFallback: false, reason: 'api_disabled'})
  }
  return store.dispatch('generalRequest', {
    type: requestType(),
    name: 'crisis',
    paramsQuery: {marketId: market},
    body: {},
    headers: {},
  }).then(unwrapSuccess).then((data) => {
    const contacts = mapCrisisResources(data.resources || [])
    return {contacts, registryUnavailable: contacts.length === 0, usedFallback: false, reason: contacts.length === 0 ? 'empty_registry' : null}
  }).catch((err) => ({contacts: [], registryUnavailable: true, usedFallback: false, reason: (err && err.code) || 'request_failed'}))
}

/**
 * @param {string} [marketId]
 * @returns {Promise<Array<{id:string,tel:string,label:string,displayName:string}>>}
 */
export function loadCrisisResources(marketId) {
  return loadCrisisResourceResult(marketId).then((result) => {
    if (!result.contacts || result.contacts.length === 0) {
      const err = new Error('CRISIS_REGISTRY_UNAVAILABLE')
      err.code = 'CRISIS_REGISTRY_UNAVAILABLE'
      err.reason = result.reason
      err.contacts = []
      throw err
    }
    return result.contacts
  })
}

/** True when urgent UI has no dialable contacts to show. */
export function crisisContactsUnavailable(contacts, registryUnavailable) {
  if (Array.isArray(contacts) && contacts.length > 0) {
    return false
  }
  return !!registryUnavailable || !Array.isArray(contacts) || contacts.length === 0
}

/** Dispositions that may show registry crisis contacts (true crisis only). */
export function dispositionShowsCrisisContacts(kind) {
  return kind === 'crisis'
}
